/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { BUILD_SEMVER } from '../../../base/common/$__metadata.js';
import { IProductConfiguration } from '../../../base/common/product.js';
import { createDecorator } from '../../instantiation/common/registry.js';

export const IProductService = createDecorator<IProductService>('productService');

export interface IProductService extends Readonly<IProductConfiguration> {

	readonly _serviceBrand: undefined;

	get<K extends keyof IProductConfiguration>(key: K): IProductConfiguration[K] | undefined;

}

export const productSchemaId = 'needle://schemas/needle-product';



// #region Product Implementation

// Declaration merging: tells TypeScript that ProductService instances have all IProductConfiguration properties
// (they are dynamically defined via Object.defineProperty in the constructor)
export interface ProductService extends Readonly<IProductConfiguration> {}
export class ProductService implements IProductService {
	readonly _serviceBrand: undefined;

	readonly [key: string]: any;

	/** @internal */
	readonly configuration: IProductConfiguration;

	constructor(conf: IProductConfiguration) {
		this.configuration = {
			...conf,
			version: conf.version || BUILD_SEMVER || '0.0.0-dev',
		};
		// Copy all configuration properties onto the instance
		for (const key of Object.keys(this.configuration)) {
			if (!(key in this)) {
				Object.defineProperty(this, key, {
					get: () => this.configuration[key as keyof IProductConfiguration],
					enumerable: true,
				});
			}
		}
	}

	get<K extends keyof IProductConfiguration>(key: K): IProductConfiguration[K] | undefined {
		return this.configuration[key];
	}

}